import { useEffect, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell, X } from 'lucide-react';
import { Badge, IconButton, Popover } from '@/components';
import {
  desktopNotificationPermission,
  notificationBellLabel,
  notificationsQueryOptions,
  readDesktopNotificationSetting,
  subscribeDesktopNotificationSettingChange,
} from '@/lib/notifications';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { DesktopNotificationBridge } from './DesktopNotificationBridge';
import { NotificationDrawer } from './NotificationDrawer';

export interface NotificationBellProps {
  readonly unreadCount?: number;
  readonly loading?: boolean;
}

export function NotificationBell({
  unreadCount,
  loading = false,
}: NotificationBellProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [open, setOpen] = useState(false);
  const [desktopEnabled, setDesktopEnabled] = useState(false);
  const desktopQuery = useQuery({
    ...notificationsQueryOptions(),
    enabled: desktopEnabled && desktopNotificationPermission() === 'granted',
  });

  useEffect(() => {
    const refresh = () => setDesktopEnabled(readDesktopNotificationSetting());
    refresh();
    return subscribeDesktopNotificationSettingChange(refresh);
  }, []);

  return (
    <>
      <DesktopNotificationBridge enabled={desktopEnabled} page={desktopQuery.data} />
      <Popover
        open={open}
        onOpenChange={setOpen}
        variant="drawer"
        placement="bottom-end"
        title={t('notifications.title')}
        trigger={
          <IconButton
            label={notificationBellLabel(unreadCount, t)}
            busy={loading}
            badge={<Badge count={unreadCount ?? 0} />}
          >
            <Bell aria-hidden="true" className="size-4" />
          </IconButton>
        }
        closeControl={
          <IconButton label={t('notifications.close')} size="sm">
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
      >
        <NotificationDrawer
          open={open}
          {...(unreadCount === undefined ? {} : { unreadCount })}
        />
      </Popover>
    </>
  );
}
